<template>
  <div class="chapter-block" :class="{'chapter-block--open': isOpen()}">
    <section
      ref="chapter"
      class="chapter-block__heading"
      :style="headingStyle"
    >
      <div
        :class="{'chapter-block__heading-wrapper--no-open': !isOpen()}"
        :tabindex="isOpen() ? '-1' : '0'"
        class="chapter-block__heading-wrapper"
        @click="conditionalClick"
        @keydown.space.prevent="conditionalClick"
      >
        <div
          ref="title"
          class="chapter-block__chapter-title"
          :inert="!isOpen()"
        >
          <AppIntersectionObserver
            :options="intersectionOptions"
            class="chapter-block__intersection-observer"
            @intersect="onIntersect"
          >
            <h2
              class="chapter-block__title"
              v-html="data.title"
            />
            <button
              v-if="isOpen()"
              class="chapter-block__button"
              aria-label="Sluit hoofdstuk"
              @click="onClick"
              @keydown.space.prevent="onClick"
            >
              <AppIconLoader
                name="closeChapter"
                variant="larger"
                aria-hidden="true"
                class="chapter-block__chapter-icon-size"
              />
            </button>
          </AppIntersectionObserver>
        </div>
        <div class="chapter-block__inner">
          <AppRichText v-if="data.text" :data="data.text" />
        </div>
      </div>
    </section>
    <div class="chapter-block__content">
      <div v-show="isOpen()" class="chapter-block__wrapper">
        <AppDynamicBlock
          :is="useBlock(block)"
          v-for="block in data.content"
          :key="`block_${block.id}`"
          :data="block"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useChapterStore } from '~/store/chapters'
import { useBlock } from '~/composables/useBlock'
import { TChapterBlock } from '~/types/block-types'
import { usePageStore } from '~/store/page'
import { useBreakpointsStore } from '~/store/breakpoints'

const props = defineProps<{
  data: TChapterBlock,
}>()

const chapter = ref<HTMLElement | null>()
const title = ref<HTMLElement | null>()

const pageStore = usePageStore()
const chapterStore = useChapterStore()
const breakPointStore = useBreakpointsStore()

const intersectionOptions = {
  threshold: '1.0',
  rootMargin: '-30% 0px -30% 0px'
}

function isMobile () {
  return breakPointStore.getCurrentBreakpointIndex < 3
}

function isOpen () {
  return chapterStore.isOpen[pageStore.page.id]
}

function onEnlarge () {
  chapter.value?.setAttribute('style', `max-height: ${title.value?.getBoundingClientRect().height + 68}px`)
}

function onReduce () {
  chapter.value?.setAttribute('style', `max-height: ${title.value?.getBoundingClientRect().height + 20}px`)
}

function onMouseEnter (e: MouseEvent) {
  if (!isOpen()) {
    onEnlarge()
  }
}

function onMouseLeave (e: MouseEvent) {
  if (!isOpen()) {
    onReduce()
  }
}

function onIntersect (data) {
  if (!isMobile() || isOpen()) {
    return
  }
  if (data.visible) {
    onEnlarge()
  } else {
    onReduce()
  }
}

onMounted(() => {
  chapter.value?.addEventListener('mouseenter', onMouseEnter)
  chapter.value?.addEventListener('mouseleave', onMouseLeave)
})

onUnmounted(() => {
  chapter.value?.removeEventListener('mouseenter', onMouseEnter)
  chapter.value?.removeEventListener('mouseleave', onMouseLeave)
})

function onScroll () {
  const yOffset = 120

  let y = chapter.value?.getBoundingClientRect().top + window.scrollY - yOffset

  if (!isOpen()) {
    y = chapter.value?.getBoundingClientRect().top + yOffset
  }

  window.scrollTo({ top: y, behavior: 'smooth' })

  // chapter.value?.scrollIntoView()
}

const onClick = (e: MouseEvent) => {
  e.stopPropagation()
  chapterStore.setIsOpen(pageStore.page.id, !isOpen())
  setTimeout(onScroll, 300) // wait for animation to end
}

const conditionalClick = (e: MouseEvent) => {
  if (!isOpen()) {
    onClick(e)
  }
}

const headingStyle = computed(() => {
  return isOpen() ? 'max-height: none;' : `max-height: ${title.value?.getBoundingClientRect().height + 20}px`
})

</script>

<style scoped lang="postcss">
.chapter-block {
  &__heading {
    @mixin app-layout-container;
    @mixin app-block-margins;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 300ms;
    transition-property: max-height;
  }

  &__heading-wrapper {
    transition-property: all;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 150ms;
    @mixin app-layout-container-column;
    @mixin app-white-block;
    box-shadow: 0 0 9px 0 #00000040;

    &--no-open {
      cursor: pointer;
    }
  }

  &__chapter-title {
    grid-column: 1 / -1;
    display: flex;
    @mixin app-intro;
  }

  &__intersection-observer {
    position: relative;
    width: 100%;
  }

  &__title {
    width: calc(100% - 32px);
  }

  &__button {
    position: absolute;
    top: -39px;
    right: 10px;
    cursor: pointer;
    border-radius: 9999px;

    @media (--lg) {
      right: -54px;
      top: -67px;
    }
  }

  &__chapter-icon-size {
    width: 30px;
    height: 30px;

    @media (--lg) {
      width: 55px;
      height: 55px;
    }
  }

  &__inner {
    margin-top: 1rem;
  }

  &__content {
    display: grid;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 300ms;
    transition-property: grid-template-rows;
    /* closed: collapsed */
    grid-template-rows: 0fr;
  }

  &__wrapper {
    /* always hide when closed or animating */
    overflow: hidden;
    /* ensure the wrapper doesn't collapse horizontally */
    width: 100%;
  }

  &--open {
    .chapter-block__content {
      grid-template-rows: 1fr;
    }

    .chapter-block__wrapper {
      /* only make visible after the 300ms height animation finishes */
      transition: overflow 0s linear 0.3s;
      overflow: visible;
    }
  }
}
</style>
